import { PRIORITY_MAP } from "@/lib/constants";
import type { Priority } from "@/lib/types";

/**
 * Palette per priorità.
 *
 * Il sito è monocromatico (accent blu) ma le priorità sono uno dei
 * pochi punti dove ha senso introdurre varietà cromatica: ogni
 * categoria editoriale ha il suo tono. Tinte sobrie, da carta stampata,
 * non saturazioni cartoon.
 *
 * Qui ci sono i NOMI delle variabili, non i colori: gli hex stanno in
 * globals.css, in due serie, `:root` per il tema chiaro e `.dark` per
 * quello scuro. Prima erano dieci hex fissi scritti in questo file e in
 * tema scuro sparivano: "focus" era #1a3a7e su fondo #070a18, cioè
 * 1,73:1, navy su navy. Un colore cablato in JS non può seguire il
 * tema, e questa è la ragione per cui non ce ne sono più.
 */
export const PRIORITY_COLORS: Record<Priority, { fg: string; soft: string }> = {
  editoriale:       { fg: "var(--p-editoriale)",      soft: "var(--p-editoriale-soft)" },      // teal
  "teatri-crisi":   { fg: "var(--p-teatri-crisi)",   soft: "var(--p-teatri-crisi-soft)" },   // crimson
  "nuovi-scenari":  { fg: "var(--p-nuovi-scenari)",  soft: "var(--p-nuovi-scenari-soft)" },  // violetto
  "segnali-deboli": { fg: "var(--p-segnali-deboli)", soft: "var(--p-segnali-deboli-soft)" }, // ambra
  ibrido:           { fg: "var(--p-ibrido)",         soft: "var(--p-ibrido-soft)" },         // teal
  focus:            { fg: "var(--p-focus)",          soft: "var(--p-focus-soft)" },          // accent
  notizie:          { fg: "var(--p-notizie)",        soft: "var(--p-notizie-soft)" },        // grigio
  report:           { fg: "var(--p-report)",         soft: "var(--p-report-soft)" },         // indaco
  pmi:              { fg: "var(--p-pmi)",            soft: "var(--p-pmi-soft)" },            // arancio
  articoli:         { fg: "var(--p-articoli)",       soft: "var(--p-articoli-soft)" },       // verde
  archivio:         { fg: "var(--p-archivio)",       soft: "var(--p-archivio-soft)" },       // grigio caldo
};

export function PriorityPill({
  priority,
  variant = "pill",
}: {
  priority: Priority;
  /** `pill` è il riquadro col bordo, giusto dove l'elemento è davvero
   *  un comando (la barra dei filtri) o una targa isolata.
   *
   *  `kicker` è la resa da carta stampata usata nelle righe del rullo:
   *  un filetto verticale colorato e la parola, senza riquadro. Nella
   *  riga di un dispaccio il riquadro prometteva un bottone che non
   *  c'era, e accanto alla categoria (anche lei riquadrata) faceva due
   *  finti comandi in fila prima ancora del titolo. */
  variant?: "pill" | "kicker";
}) {
  const p = PRIORITY_MAP[priority];
  const c = PRIORITY_COLORS[priority];

  if (variant === "kicker") {
    return (
      <span
        className="inline-flex items-center gap-2 whitespace-nowrap text-[12px] font-bold uppercase tracking-[0.12em]"
        style={{ color: c.fg }}
      >
        <span
          aria-hidden
          className="inline-block h-[11px] w-[3px] flex-shrink-0"
          style={{ backgroundColor: c.fg }}
        />
        {p.label}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center rounded-sm border bg-transparent px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.12em] whitespace-nowrap transition-colors hover:bg-[var(--p-soft)]"
      style={
        {
          color: c.fg,
          borderColor: c.fg,
          ["--p-soft" as string]: c.soft,
        } as React.CSSProperties
      }
    >
      {p.label}
    </span>
  );
}
